import { useEffect, useRef, useState } from 'react';
import ProfileCard from "../../../../components/ProfileCard";
import "./About.scss"

function useInView(threshold = 0.20) {
    const ref = useRef<HTMLDivElement>(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
            { threshold }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, inView] as const;
}

const tools = [
    { name: 'Figma', icon: '/icons/figma.svg' },
    { name: 'Sketch', icon: '/icons/sketch.png' },
    { name: 'Lightroom', icon: '/icons/lightroom.png' },
    { name: 'Illustrator', icon: '/icons/illustrator.png' },
    { name: 'CSS', icon: '/icons/css.svg' },
    { name: 'HTML', icon: '/icons/html.svg' }
];

function About() {
    const [leftRef, leftInView] = useInView(0.2);
    const [rightRef, rightInView] = useInView(0.15);
    const [skillsRef, skillsInView] = useInView(0.2);
    const [hoveredTool, setHoveredTool] = useState<number | null>(null);

    return (
        <section className="about">
            <div className="background"></div>

            <div className="container">
                <div className="content">
                    <div
                        ref={leftRef}
                        className={`leftColumn ${leftInView ? 'in-view' : ''}`}
                    >
                        <ProfileCard
                            className="profile-card"
                            name="Mayank Sharma"
                            title="UI/UX Designer"
                            avatarUrl="/images/self.png"
                            showUserInfo={false}
                            enableTilt={true}
                            enableMobileTilt={false}
                            behindGlowEnabled={true}
                            behindGlowSize="30%"
                            behindGlowColor="rgba(125, 190, 255, 0.67)"
                            innerGradient="linear-gradient(145deg,#60496e8c 0%,#71C4FF44 100%)"
                        />
                    </div>

                    <div ref={rightRef} className="rightColumn">
                        <div className={`section ${rightInView ? 'in-view' : ''}`}>
                            <span className="ab__eyebrow">ABOUT ME</span>
                            <h2 className="ab__headline">Designing with</h2>
                            <span className="ab__headline-em">Purpose & Precision.</span>

                            <p className="bio">
                                Hi, I'm a UI/UX designer with a frontend development background, driven by a strong interest in how people interact with digital products.
                                I enjoy working at the intersection of design and engineering, translating user needs into clear,
                                accessible interfaces that are practical to build and easy to use. With experience in frontend development,
                                I approach design with a system-level mindset, paying close attention to structure, interaction, and detail.
                                I'm especially interested in designing experiences that prioritize clarity, usability, and ease of interaction.
                            </p>
                        </div>

                        <div className="ab__rule" />

                        <div className={`section ${rightInView ? 'in-view' : ''}`} style={{marginBottom: '1rem'}}>
                            <h4 className="skills">SKILLS</h4>
                            <div ref={skillsRef} className="ab__skills">
                                {["UI Design", "UX Research", "Prototyping", "Design Systems",
                                    "Interaction Design", "Visual Design", "Wireframing", "User Testing"].map(s => (
                                        <span
                                            key={s}
                                            className={`ab__skill ${skillsInView ? 'in-view' : ''}`}
                                        >
                                            {s}
                                        </span>
                                    ))}
                            </div>
                        </div>

                        <div className={`section ${rightInView ? 'in-view' : ''}`}>
                            <h4 className="skills">TOOLS</h4>

                            <div className="glass-icons">
                                {tools.map((tool, index) => (
                                    <div
                                        key={tool.name}
                                        className="glass-icon"
                                        onMouseEnter={() => setHoveredTool(index)}
                                        onMouseLeave={() => setHoveredTool(null)}
                                    >
                                        <div className="glass-icon__card">
                                            <div className="glass-icon__glow"></div>
                                            <div className="glass-icon__content">
                                                <img
                                                    src={tool.icon}
                                                    alt={tool.name}
                                                    className="glass-icon__image"
                                                />
                                            </div>
                                        </div>
                                        <span
                                            className={`glass-icon__label ${hoveredTool === index ? 'visible' : ''}`}
                                        >
                                            {tool.name}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className={`section ${rightInView ? 'in-view' : ''}`}>
                            <div className="twoColumnSection">
                                <div className="column">
                                    <h4 className="subsectionTitle">EDUCATION</h4>
                                    <p className="detail">B.Tech.</p>
                                    <p className="detailSmall">
                                        Electrical & Electronics Engineering
                                    </p>
                                </div>
                                <div className="column">
                                    <h4 className="subsectionTitle">EXPERIENCE</h4>
                                    <p className="detail">UI/UX Designer & Frontend Developer</p>
                                    <p className="detailSmall">
                                        Designing intuitive interfaces, built with real-world constraints
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default About;